import {
  getOutcomeResultForValueId,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import type { OutcomeSampleGradingEntry } from "@repo/app-types/entities/playbookProposalDetail";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import { Size } from "@repo/ui/helpers/helpers";
import { OutcomeResultPill } from "@repo/ui/Pills/outcomeResultPill";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { type FC, type ReactNode, useMemo } from "react";
import { GradingsTableContactCell } from "../../features/teammatePerformance/gradingsTableContactCell.js";
import { TeammateNameStack } from "../../features/teammatePerformance/teammateNameStack.js";

interface OutcomeSampleGradingsTableProps {
  entries: readonly OutcomeSampleGradingEntry[];
  outcomeDefinition: OutcomeDefinition;
  isLoading?: boolean;
  isError?: boolean;
  onGradingSelect: (gradingId: GradingId) => void;
}

type OutcomeSampleGradingRow = OutcomeSampleGradingEntry & {
  id: string;
};

export const OutcomeSampleGradingsTable: FC<OutcomeSampleGradingsTableProps> = (
  props,
) => {
  const { entries, outcomeDefinition } = props;

  const rows = useMemo(
    (): OutcomeSampleGradingRow[] =>
      entries.map((e) => ({ ...e, id: e.grading.id })),
    [entries],
  );

  const columns = useMemo(
    (): EntityTableColumnDefinition<OutcomeSampleGradingRow>[] => [
      {
        id: "customer",
        className: "w-[28%]",
        header: "Customer",
        isRowHeader: true,
        renderCell: (entry) => (
          <GradingsTableContactCell grading={entry.grading} />
        ),
      },
      {
        id: "teammate",
        className: "w-[20%]",
        header: "Teammate",
        renderCell: (entry) => <TeammateNameStack teammate={entry.teammate} />,
      },
      {
        id: "currentGrade",
        className: "w-0",
        header: "Current grade",
        renderCell: (entry) =>
          renderOutcomeSampleGrade(outcomeDefinition, entry.baseline_grade),
      },
      {
        id: "testGrade",
        className: "w-0",
        header: "Test grade",
        renderCell: (entry) =>
          renderOutcomeSampleGrade(outcomeDefinition, entry.tested_grade),
      },
      {
        id: "testCommentary",
        className: "w-[50%]",
        header: "Test commentary",
        renderCell: (entry) => (
          <div>{entry.tested_grade?.commentary ?? "—"}</div>
        ),
      },
    ],
    [outcomeDefinition],
  );

  return (
    <EntityTable
      aria-label="Graded calls"
      columns={columns}
      entities={rows}
      isLoading={props.isLoading}
      isError={props.isError}
      emptyMessage="No graded calls in this sample."
      size={Size.SMALL}
      isFullBleed
      onEntityAction={(entry) => props.onGradingSelect(entry.grading.id)}
    />
  );
};

export function renderOutcomeSampleGrade(
  outcomeDefinition: OutcomeDefinition,
  grade: { value: string } | null,
): ReactNode {
  if (!grade) return "—";

  const result = getOutcomeResultForValueId(outcomeDefinition, grade.value);
  return result ? (
    <OutcomeResultPill size={Size.SMALL} result={result} />
  ) : (
    grade.value
  );
}
